import type { CSSProperties } from "react";

// Shared, fixed-color chrome for overlays drawn on top of a video tile's own
// media, split out of VideoTile.tsx purely so that file stays a pure
// component export — Vite's Fast Refresh can't hot-patch a component file
// that also exports a plain value, and falls back to a full reload on every
// edit.
//
// The tile's header strip itself no longer lives here: it is a plain
// absolutely-positioned bar drawn directly in VideoTile.tsx, using this same
// `--color-overlay` token — a fixed light-dark() pair designed for scrims
// over arbitrary media (the same one Thumbnail's own remove-button overlay
// uses), not a themed surface color that would go translucent-wrong over a
// bright frame.

// Every absolutely-positioned layer inside a VideoTile's 16:9 frame, in paint
// order. DOM order deliberately does NOT decide this — see tileLayer below.
//
// The tile stacks these layers on top of one `<video>`, and has gained
// roughly one per feature (header, status panel, play gesture, PTZ pad,
// bottom bar). When stacking is DOM order, adding a layer in the natural
// place — the bottom of the JSX — silently puts it over the header, and the
// symptom is a control that stops responding to clicks: no error, no type
// error, no visual change, nothing a reviewer sees in a diff. That is exactly
// how the status panel came to cover the header's Play/Stop/fullscreen
// buttons in every non-playing state, while a comment three lines into that
// same panel's JSX documented relying on that Play button as the compact
// tile's only retry path.
//
// Two rules, both encoded in the table below:
//
//  1. VISIBLE MEANS CLICKABLE. Chrome the tile actually draws — the header,
//     the bottom bar, the PTZ pad — sits above every full-frame layer. A
//     layer that must not be operated (the PTZ pad over a frozen frame, say)
//     is not rendered at all, never merely buried under something else.
//
//  2. A PASSIVE FULL-FRAME LAYER IS TRANSPARENT TO THE POINTER.
//     `isPointerTarget: false` puts `pointer-events: none` on the layer's own
//     wrapper; the one inner box that actually holds a control (the status
//     panel's card, holding Retry) re-enables the pointer with
//     `tileLayerContentStyle`. This is the same shape Astryx's own
//     `OverlayScrim` uses: the scrim wrapper ignores the pointer, the content
//     inside it does not.
//
// `tileLayer()` spreads a caller's own style *before* zIndex/pointerEvents,
// so a call site cannot quietly opt out of either rule. `Record<TileLayer, …>`
// means a new layer is a compile error until it is placed here — the same
// total-mapping discipline playerCopy.ts's `Record<PlayerErrorCode, …>`
// already uses for exactly this reason: the alternative is a decision that
// can be silently skipped.
export type TileLayer =
  | "video"
  | "statusPanel"
  | "pausedFrame"
  | "playGesture"
  | "ptzPad"
  | "bottomBar"
  | "header";

interface TileLayerSpec {
  z: number;
  /** False = full-frame and passive: must let clicks/taps through to
   *  whatever chrome is drawn above it. */
  isPointerTarget: boolean;
}

const TILE_LAYERS: Record<TileLayer, TileLayerSpec> = {
  video: { z: 0, isPointerTarget: false },
  statusPanel: { z: 1, isPointerTarget: false },
  pausedFrame: { z: 2, isPointerTarget: false },
  // The one genuine full-frame click target: the browser's autoplay policy
  // needs a user gesture, and the whole frame is the gesture. It is a
  // pointer target on purpose, so pointer-events cannot resolve its overlap
  // with the header — only z-order does. It owns every pixel no other
  // control claims, which is why it sits below the header/bottomBar/ptzPad
  // rather than above them.
  playGesture: { z: 3, isPointerTarget: true },
  ptzPad: { z: 4, isPointerTarget: true },
  bottomBar: { z: 5, isPointerTarget: true },
  header: { z: 6, isPointerTarget: true },
};

/**
 * Spread onto a layer's root element. The layer's own position/inset/etc. go
 * in `style`; zIndex and pointerEvents are appended AFTER it on purpose, so
 * they always win over whatever a call site passes.
 */
export function tileLayer(layer: TileLayer, style?: CSSProperties) {
  const spec = TILE_LAYERS[layer];
  return {
    // Also what a structural test asserts against, and what makes the stack
    // legible in DevTools on a tile with six overlapping layers.
    "data-tile-layer": layer,
    style: {
      position: "absolute" as const,
      ...style,
      zIndex: spec.z,
      pointerEvents: spec.isPointerTarget ? undefined : ("none" as const),
    },
  };
}

/** Re-enables the pointer for the one interactive box inside an otherwise
 *  passive full-frame layer — the status panel's card, which holds Retry. */
export const tileLayerContentStyle = { pointerEvents: "auto" as const };

// How far a full-frame layer must inset from the top to clear the header
// strip drawn inside the video (VideoTile.tsx). Measured ~36px, plus a
// visible gap and headroom for a slightly taller one. Shared rather than
// hand-copied so the PTZ pad and the status panel cannot drift apart the way
// the pad's own offset already has once — see its git history.
export const TILE_HEADER_CLEARANCE = "3.5rem";

// Anchors the PTZ pad over the video, top-right.
//
// Top rather than bottom, which is where video controls normally live, for a
// concrete reason: the focus dialog sizes its video by aspect ratio at full
// width, so on any window that is wide relative to its height the bottom of
// the frame sits below the fold. A bottom-anchored pad is correctly placed
// inside the video and still invisible without scrolling — which for the
// primary control of a moving camera is worse than unconventional. The top
// edge is on screen whatever the window's shape.
//
// `insetBlockStart` clears the tile's own header (drawn inside the frame,
// see VideoTile.tsx), not just the frame's edge — see TILE_HEADER_CLEARANCE.
//
// It also keeps the pad clear of the bottom scrim bar entirely, so the mute
// button and the pad never compete for the same pixels.
//
// `color: "white"` stays a literal here (not "residue" — PtzPad's own
// supporting text reads it via `color="inherit"`, which is genuine CSS color
// inheritance, not an Astryx token lookup that MediaTheme could resolve on
// its own). Only the background became the shared overlay token.
//
// position/z-index/pointer-events are no longer this object's concern — the
// `ptzPad` entry in TILE_LAYERS owns them, applied via `tileLayer("ptzPad", …)`
// at the call site. This object only carries the pad's own visual styling.
export const ptzOverlayStyle = {
  insetInlineEnd: "var(--spacing-2)",
  insetBlockStart: TILE_HEADER_CLEARANCE,
  background: "var(--color-overlay)",
  borderRadius: "var(--radius-container)",
  color: "white",
} as const;
